import { sortBy, uniq } from 'lodash-es';
import { type Reducer, useEffect, useReducer } from 'react';
import { z } from 'zod';
import { useShallow } from 'zustand/react/shallow';
import { ConversationMessageContentType, type ConversationMessageTypeUnion } from '@/models/conversations/conversation';
import { useMessageStore } from '@/stores/message';

export default function useMediaMessages(
  messageId?: Nullable<number>,
  direction?: 'forward' | 'backward',
  filterType: MediaMessageFilterType[] = ['all'],
) {
  const messages = useMessageStore(useShallow((s) => Array.from(s.messages.values())));

  const [state, dispatch] = useReducer<MediaMessageReducer>(
    mediaMessageReducer,
    createInitialState(messages, filterType, direction),
  );

  const messageIndex = messageId ? state.messages.findIndex((m) => m.ID === messageId) : -1;

  const message = messageIndex > -1 ? state.messages.at(messageIndex) : undefined;

  useEffect(() => {
    dispatch({
      event: 'update-messages',
      params: { messages },
    });
  }, [messages]);

  useEffect(() => {
    let filters: MediaMessageFilterType[] = filterType;

    if (message?.MsgType) {
      switch (message.MsgType) {
        case ConversationMessageContentType.File:
          filters = ['files'];
          break;
        case ConversationMessageContentType.Image:
        case ConversationMessageContentType.Video:
          filters = ['images', 'videos'];
          break;
        default:
          filters = ['all'];
          break;
      }
    }
    dispatch({
      event: 'update-filter',
      params: { filterType: filters },
    });
  }, [filterType.length, message?.MsgType]);

  return {
    messages: state.messages,
    messageIndex,
    message,
    filteredBy: state.filteredBy,
    setFilter: (type: MediaMessageFilterType[]) =>
      dispatch({ event: 'update-filter', params: { filterType: type, messages } }),
  };
}

type MediaMessageReducerAction =
  | {
      event: 'update-filter';
      params: {
        filterType: MediaMessageFilterType[];
        messages?: ConversationMessageTypeUnion[];
      };
    }
  | {
      event: 'update-direction';
      params: {
        direction: 'forward' | 'backward';
        messages?: ConversationMessageTypeUnion[];
      };
    }
  | {
      event: 'update-messages';
      params: {
        messages: ConversationMessageTypeUnion[];
      };
    }
  | {
      event: 'reset';
      params: Partial<{
        messages: ConversationMessageTypeUnion[];
        filterType: MediaMessageFilterType | MediaMessageFilterType[];
        direction: 'forward' | 'backward';
      }>;
    };

const mediaFilterOptions = ['all', 'images', 'videos', 'files'] as const;
export const mediaFilterOptionsSchema = z.enum(mediaFilterOptions);
export type MediaMessageFilterType = z.infer<typeof mediaFilterOptionsSchema>;

type MediaMessageReducerState = {
  direction: 'forward' | 'backward';
  filteredBy: MediaMessageFilterType[];
  messages: ConversationMessageTypeUnion[];
};

type MediaMessageReducer = Reducer<MediaMessageReducerState, MediaMessageReducerAction>;

function createInitialState(
  messages: ConversationMessageTypeUnion[],
  filterType: MediaMessageFilterType[],
  direction: 'forward' | 'backward' = 'forward',
): MediaMessageReducerState {
  const filteredBy: MediaMessageFilterType[] = filterType.length > 0 ? uniq(filterType) : ['all'];

  return mediaMessageReducer(
    {
      filteredBy,
      direction,
      messages: [],
    },
    {
      event: 'reset',
      params: { messages, filterType: filteredBy, direction },
    },
  );
}

function mediaMessageReducer(
  state: MediaMessageReducerState,
  action: MediaMessageReducerAction,
): MediaMessageReducerState {
  let newState = state;

  switch (action.event) {
    case 'reset':
      newState = {
        ...state,
        ...action.params,
      };
      break;
    case 'update-filter':
      newState = {
        ...state,
        filteredBy: action.params.filterType.length > 0 ? uniq(action.params.filterType) : ['all'],
        messages: action.params.messages ?? state.messages,
      };
      break;
    case 'update-direction':
      newState = {
        ...state,
        direction: action.params.direction,
        messages: action.params.messages ?? state.messages,
      };
      break;
    case 'update-messages':
      newState = {
        ...state,
        messages: action.params.messages,
      };
      break;
    default:
      return state;
  }

  newState.messages = filterMediaMessagesByType(newState);

  return newState;
}

function filterMediaMessagesByType({ messages, filteredBy, direction }: MediaMessageReducerState) {
  const sortedMessages = sortBy(messages, (m) => (direction === 'forward' ? m.CreateTime : -m.CreateTime));
  const filterTypes = typeof filteredBy === 'string' ? [filteredBy] : filteredBy;

  const filterMessageTypes = uniq(
    filteredBy.flatMap((type) => {
      switch (type) {
        case 'files':
          return ConversationMessageContentType.File;
        case 'images':
          return ConversationMessageContentType.Image;
        case 'videos':
          return ConversationMessageContentType.Video;
        default:
          return [
            ConversationMessageContentType.File,
            ConversationMessageContentType.Image,
            ConversationMessageContentType.Video,
          ];
      }
    }),
  );

  return sortedMessages.filter((m) => filterMessageTypes.includes(m.MsgType));
}
